/**
 * Session Leaders API service — MED-906 / E4.0c
 *
 * Backs the Session Leaders tab on the Cohort Detail page (replaces the
 * Coaches tab for peer_led group_coaching cohorts).
 *
 * Endpoints:
 *   GET  /api/cohorts/:cohortId/sessions/leaders-grid                — grid data
 *   POST /api/cohorts/:cohortId/sessions/generate-leader-rotation    — rotation proposal
 *   POST /api/cohorts/:cohortId/sessions/finalize-leader-rotation    — commit batch
 *   GET  /api/cohorts/:cohortId/sessions/leader-rotation-availability — AC7 availability rows
 *
 * Per-cell leader edits use the existing PUT
 *   /api/cohorts/:cohortId/sessions/:sessionId/leader   (MED-812)
 * exposed via `sessionsApi` and is NOT duplicated here.
 */

import apiClient from './client';

export interface SessionLeadersGridLeader {
  user_id: string;
  first_name: string;
  last_name: string;
  avatar_url: string | null;
}

export interface SessionLeadersGridSession {
  session_id: string;
  session_number: number | null;
  topic_name: string | null;
  scheduled_start: string | null;
  scheduled_end: string | null;
  group_id: string;
  leader: SessionLeadersGridLeader | null;
}

export interface SessionLeadersGridGroupMember {
  user_id: string;
  first_name: string;
  last_name: string;
}

export interface SessionLeadersGridGroup {
  group_id: string;
  name: string;
  member_count: number;
  members: SessionLeadersGridGroupMember[];
}

export interface SessionLeadersGridResponse {
  groups: SessionLeadersGridGroup[];
  sessions: SessionLeadersGridSession[];
}

export interface RotationProposalLeader {
  user_id: string;
  first_name: string;
  last_name: string;
  ivse_average: number;
  prep_preference_average: number;
}

export interface RotationProposalAssignment {
  session_id: string;
  session_number: number | null;
  topic_name: string | null;
  scheduled_start: string | null;
  group_id: string;
  group_name: string;
  proposed_leader: RotationProposalLeader;
}

export interface RotationSkippedParticipant {
  user_id: string;
  first_name: string;
  last_name: string;
  group_id: string;
  group_name: string;
  reason: 'no_intake_submission' | 'incomplete_intake_answers';
  ivse_answered: number;
  ivse_total: number;
  prep_answered: number;
  prep_total: number;
}

export interface RotationGroupWarning {
  group_id: string;
  group_name: string;
  code: string;
  level: 'info' | 'warning';
  message: string;
}

export interface RotationProposalResponse {
  assignments: RotationProposalAssignment[];
  skipped_participants: RotationSkippedParticipant[];
  warnings: RotationGroupWarning[];
}

export interface FinalizeAssignment {
  session_id: string;
  leader_id: string;
}

export interface FinalizeRotationResponse {
  finalized_count: number;
  grid: SessionLeadersGridResponse;
}

export interface AvailabilityRowParticipant {
  user_id: string;
  first_name: string;
  last_name: string;
  availability: string | null;
  available_time_slots: string[];
  availability_notes: string | null;
}

export interface AvailabilityRowGroup {
  group_id: string;
  participants: AvailabilityRowParticipant[];
}

export interface AvailabilityResponse {
  groups: AvailabilityRowGroup[];
}

// MED-1009 / E4.0e: Participants subpage + refined Schedule drawer rationale.
// Same intake-derived signals the E4.0c algorithm consumes, surfaced read-only.
export interface ParticipantRationaleTopicExcitement {
  session_number: number;
  topic_name: string;
}

export interface ParticipantRationaleEntry {
  user_id: string;
  first_name: string;
  last_name: string;
  ivse_average: number | null;
  prep_preference_average: number | null;
  topic_excitement: ParticipantRationaleTopicExcitement[];
  prior_experience: {
    has_experience: boolean;
    choices: string[];
  };
  availability_notes: string | null;
}

export interface ParticipantRationaleGroup {
  group_id: string;
  group_name: string;
  member_count: number;
  participants: ParticipantRationaleEntry[];
}

export interface ParticipantsRationaleResponse {
  groups: ParticipantRationaleGroup[];
}

export const sessionLeadersApi = {
  /**
   * Fetch the Session Leaders grid for a peer_led cohort.
   * Throws on 404 (cohort missing / feature flag off) or 422 (not peer_led).
   */
  getGrid: async (cohortId: string): Promise<SessionLeadersGridResponse> => {
    return apiClient.get<SessionLeadersGridResponse>(
      `/api/cohorts/${cohortId}/sessions/leaders-grid`,
    );
  },

  /**
   * Generate a non-persisted rotation proposal. The FE renders this and lets
   * the CM edit per cell before finalizing. Resolves with the proposal payload
   * including skipped participants and any per-group warnings (e.g.
   * single-member group → unavoidable back-to-back).
   */
  generateRotation: async (cohortId: string): Promise<RotationProposalResponse> => {
    return apiClient.post<RotationProposalResponse>(
      `/api/cohorts/${cohortId}/sessions/generate-leader-rotation`,
    );
  },

  /**
   * Commit a (possibly CM-edited) rotation proposal to session records.
   * Server applies all writes atomically and returns the refreshed grid.
   */
  finalizeRotation: async (
    cohortId: string,
    assignments: FinalizeAssignment[],
  ): Promise<FinalizeRotationResponse> => {
    return apiClient.post<FinalizeRotationResponse>(
      `/api/cohorts/${cohortId}/sessions/finalize-leader-rotation`,
      { assignments },
    );
  },

  /**
   * Fetch per-group participant availability + open-text notes for the
   * rotation review drawer (AC7). Surfaced to the CM as informational context;
   * not persisted, not used as an algorithmic tiebreaker.
   */
  getAvailability: async (cohortId: string): Promise<AvailabilityResponse> => {
    return apiClient.get<AvailabilityResponse>(
      `/api/cohorts/${cohortId}/sessions/leader-rotation-availability`,
    );
  },

  /**
   * MED-1009 / E4.0e: Per-group participant rationale backing the Participants
   * subpage and the refined Schedule drawer. Read-only — the same intake-derived
   * scores the rotation algorithm uses (IvsE, prep, topic-excitement,
   * prior-experience) plus the free-text availability note.
   */
  getParticipantsRationale: async (cohortId: string): Promise<ParticipantsRationaleResponse> => {
    return apiClient.get<ParticipantsRationaleResponse>(
      `/api/cohorts/${cohortId}/sessions/leader-rotation-participants`,
    );
  },
};
